/** DESK V3 tile library — every module is a placeable tile. Internal tool. */
import React, { useEffect, useState, useRef } from 'react';
import { DUR, EASE, reduced } from './studioMotion';
import { DemoBadge } from './V2DeskLists';

// TravelTrucks Aero palette — charcoal canvas, orange accents, green for wins.
export const BG = '#0E0F11', PANEL = 'rgba(255,255,255,0.06)', BORDER = 'rgba(255,255,255,0.10)';
export const TXT = '#F1F2F3', SUB = '#A7ABB2', TEAL = '#54D197', AMBER = '#F79640', MAGENTA = '#EC7C24', VIOLET = '#EC7C24';
export const SPEC = `linear-gradient(135deg, #F79640, #EC7C24, #C85A12)`;
export const HEAD = "'Fredoka', 'Source Sans 3', sans-serif";
export const HEAT = { fresh: TEAL, warm: AMBER, hot: MAGENTA };
export const TYPE_META = { client: ['👤', TEAL], task: ['⚡', '#7BB0EC'], note: ['📝', AMBER], job: ['🔧', VIOLET], idea: ['💡', '#FFB469'] };
export const mini = { fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 700, color: TXT, background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '4px 11px', cursor: 'pointer', transition: `all ${DUR.fast}ms ${EASE.out}` };

/** Demo-mode scrubber — render-level only, never touches the DB. */
const PSEUDO = [
  'a Brisbane cleaning business', 'a Gold Coast plumbing outfit', 'a Sunshine Coast café',
  'a Logan electrician', 'a Redlands landscaper', 'a Bayside pool service',
  'an Ipswich builder', 'a CBD accounting firm', 'a Northside gym', 'a Moreton Bay florist',
];
const hashStr = (s) => { let x = 0; for (let i = 0; i < s.length; i++) x = (x * 31 + s.charCodeAt(i)) >>> 0; return x; };
export const makeScrub = (clients, extraNames = []) => {
  const map = [];
  (clients || []).forEach((c) => {
    const p = PSEUDO[hashStr(c.item_id || c.title || '') % PSEUDO.length];
    if (c.title) map.push([c.title, p]);
    const first = (c.title || '').split(' ')[0];
    if (first && first.length > 2) map.push([first, p]);
  });
  (extraNames || []).forEach((n) => {
    if (n && n.length > 2) map.push([n, PSEUDO[hashStr(n) % PSEUDO.length]]);
  });
  map.sort((a, b) => b[0].length - a[0].length);
  return (text) => {
    if (text == null) return text;
    let s = String(text);
    map.forEach(([n, p]) => { s = s.split(n).join(p); });
    return s
      .replace(/\$\s?[\d,]+(\.\d+)?k?/gi, '$•••')
      .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '•••@•••');
  };
};


export const CountUp = ({ value, prefix = '$' }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (reduced() || !value) { setV(value || 0); return; }
    let raf; const t0 = performance.now();
    const step = (t) => { const p = Math.min(1, (t - t0) / 900); setV(value * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{prefix}{Math.round(v).toLocaleString()}</>;
};

export const Spark = ({ data, color = TEAL, w = 60, h = 18 }) => {
  const max = Math.max(1, ...data);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - 2 - (v / max) * (h - 5)}`).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round"
        style={reduced() ? {} : { strokeDasharray: 200, strokeDashoffset: 0, animation: 'desk-draw 900ms ease-out' }} />
    </svg>
  );
};

export const ArcRing = ({ days, size = 52 }) => {
  const p = Math.min(1, days / 10), r = size / 2 - 6, c = 2 * Math.PI * r;
  const col = days < 3 ? TEAL : days < 7 ? AMBER : MAGENTA;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="4" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth="4" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - Math.max(0.04, p))} transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: `stroke-dashoffset 800ms ${EASE.out}` }} />
      <text x={size / 2} y={size / 2 + 4} textAnchor="middle" fontFamily="Source Sans 3, sans-serif" fontSize="12" fontWeight="800" fill={col}>{Math.round(days)}d</text>
    </svg>
  );
};

const PULSE_ICON = { reply: '↩', user: '👤', 'file-check': '📄', zap: '⚡', wrench: '🔧' };

export const NowTile = ({ ctx }) => {
  const now = ctx.board?.now;
  if (!now) return <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: SUB, margin: 0 }}>All clear — nothing needs you right now. ✨</p>;
  return (
    <div data-testid="desk-now-card" style={{ display: 'flex', gap: 16, alignItems: 'center', height: '100%' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <h2 style={{ fontFamily: HEAD, fontSize: 22, fontWeight: 800, color: TXT, margin: 0, lineHeight: 1.2 }}>{ctx.dm(now.title)} <DemoBadge seed={now.seed} /></h2>
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: AMBER, margin: '4px 0 0' }}>{ctx.dm(now.sub)}</p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button data-testid="now-do-it" onClick={() => ctx.pulseAction(now)}
            style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, fontWeight: 800, color: '#FFFFFF', background: SPEC, border: 'none', borderRadius: 11, padding: '9px 17px', cursor: 'pointer' }}>
            {now.action_type === 'focus' ? 'Do it — focus mode' : now.action}
          </button>
          {now.action_type !== 'focus' && (
            <button data-testid="now-focus-btn" onClick={() => ctx.enterFocus(now)} title="tunnel-vision this"
              style={{ ...mini, padding: '9px 14px', fontSize: 11.5, color: VIOLET, borderColor: 'rgba(236,124,36,0.5)' }}>◎ Focus</button>
          )}
          <button data-testid="now-done" onClick={(e) => ctx.nowDone(e)} title="Mark done — sweep to wins, promote next"
            style={{ ...mini, padding: '9px 14px', fontSize: 11.5, color: TEAL, borderColor: 'rgba(84,209,151,0.5)' }}>✓ Done</button>
          <button data-testid="now-not-now" onClick={ctx.demoteNow} style={{ ...mini, padding: '9px 14px', fontSize: 11.5 }}>Not now ↓</button>
        </div>
      </div>
      <ArcRing days={now.day_arc || 0} size={78} />
    </div>
  );
};

export const PulseTile = ({ ctx }) => {
  const rows = (ctx.board?.pulse || []).slice(ctx.board?.now ? 1 : 0);
  if (!rows.length) return <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, margin: 0 }}>All quiet after the NOW card. ✨</p>;
  return rows.map((p, i) => (
    <div key={i} data-testid={`pulse-row-${i}`} className="desk-card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', margin: '4px 0', borderRadius: 11, background: 'rgba(255,255,255,0.035)', border: p.hot ? '1px solid transparent' : `1px solid ${BORDER}`, ...(p.hot ? { backgroundImage: `linear-gradient(rgba(23,24,27,0.96), rgba(23,24,27,0.96)), ${SPEC}`, backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box' } : {}) }}>
      <span style={{ width: 25, height: 25, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.07)', fontSize: 12, flexShrink: 0 }}>{PULSE_ICON[p.icon] || '•'}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, fontWeight: 700, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctx.dm(p.title)} <DemoBadge seed={p.seed} /></p>
        <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: p.hot ? MAGENTA : AMBER }}>{ctx.dm(p.sub)}</p>
      </div>
      <button data-testid={`pulse-action-${i}`} onClick={() => ctx.pulseAction(p)} className="no-drag" style={{ ...mini, flexShrink: 0, padding: '3px 10px', fontSize: 10, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>{p.action}</button>
    </div>
  ));
};

export const ClientCard = ({ c, ctx, compact = false }) => {
  const hc = HEAT[c.heat] || TEAL;
  return (
    <div data-testid={`client-card-${c.item_id}`} className={`desk-card${c.heat === 'hot' ? ' desk-heat-hot' : ''}`}
      onClick={(e) => { if (e.target.closest('button')) return; ctx.openClient(c.item_id); }}
      style={{ background: PANEL, border: `1px solid ${hc}55`, borderRadius: 12, padding: '10px 11px', boxShadow: `0 4px 16px ${hc}22`, minWidth: 0, cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', fontFamily: HEAD, fontWeight: 800, fontSize: 12, color: '#FFFFFF', background: SPEC, border: `2px solid ${hc}` }}>
          {(ctx.dm(c.title) || '?').slice(0, 1).toUpperCase()}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, fontWeight: 800, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctx.dm(c.title)} <DemoBadge seed={c.seed} /></p>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 7.5, fontWeight: 800, letterSpacing: '0.12em', color: hc, border: `1px solid ${hc}66`, borderRadius: 999, padding: '0px 6px' }}>{c.chip} · {Math.round(c.stale_days)}d</span>
        </div>
        {!compact && <Spark data={c.spark || []} color={hc} w={38} h={14} />}
      </div>
      <p style={{ margin: '7px 0 2px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <b style={{ color: AMBER }}>NEXT:</b> {ctx.dm(c.next)}
      </p>
      <div className="no-drag" style={{ display: 'flex', gap: 4, marginTop: 6 }}>
        <button data-testid={`client-go-${c.item_id}`} onClick={() => ctx.openClient(c.item_id)} style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.4)', padding: '2px 9px', fontSize: 10 }}>Open →</button>
        <button data-testid={`client-edit-${c.item_id}`} onClick={() => ctx.setEditItem({ ...c })} style={{ ...mini, padding: '2px 8px', fontSize: 10 }}>✎</button>
        <button data-testid={`client-archive-${c.item_id}`} onClick={() => ctx.setState(c, 'archived', `⤓ Archived "${c.title}"`)} style={{ ...mini, padding: '2px 8px', fontSize: 10 }}>⤓</button>
        <button data-testid={`client-delete-${c.item_id}`} onClick={() => ctx.setDelConfirm(c)} style={{ ...mini, padding: '2px 8px', fontSize: 10, color: MAGENTA, borderColor: 'rgba(236,124,36,0.4)' }}>🗑</button>
      </div>
    </div>
  );
};

export const ClientsTile = ({ ctx }) => {
  const list = ctx.board?.clients || [];
  if (!list.length) return <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, margin: 0 }}>No client cards yet — capture one.</p>;
  return (
    <div data-testid="desk-clients" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: 8 }}>
      {list.map((c) => <ClientCard key={c.item_id} c={c} ctx={ctx} />)}
    </div>
  );
};

export const SingleClientTile = ({ ctx, itemId }) => {
  const c = (ctx.board?.clients || []).find((x) => x.item_id === itemId);
  if (!c) return <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, margin: 0 }}>Client not on the board (archived?). Remove this tile or restore them.</p>;
  return <ClientCard c={c} ctx={ctx} />;
};

export const MoneyTile = ({ ctx }) => {
  const m = ctx.board?.money || {};
  const trendColor = m.forecast_trend === 'up' ? TEAL : (m.forecast_trend === 'down' ? '#EC7C24' : SUB);
  const trendArrow = m.forecast_trend === 'up' ? '↗' : (m.forecast_trend === 'down' ? '↘' : '→');
  return (
    <div data-testid="money-ticker" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', height: '100%' }}>
      {[['Quotes out', ctx.demo ? '$•••' : <CountUp key="q" value={m.quotes_out || 0} />, TEAL, 'money-quotes-out', 'Raw sum of open quote totals (incl. GST)'],
        ['Likely this month', ctx.demo ? '$•••' : <CountUp key="l" value={m.likely_this_month || 0} />, '#EC7C24', 'money-likely', 'Forecast: sum(each open quote × stage close-probability)'],
        ['Won this month', ctx.demo ? '$•••' : <CountUp key="w" value={m.won_month || 0} />, AMBER, 'money-won', 'Accepted quotes with accepted_at in this calendar month']].map(([l, v, c, tid, tt]) => (
        <div key={l} title={tt}>
          <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>{l}</p>
          <p data-testid={tid} style={{ margin: 0, fontFamily: HEAD, fontSize: 17, fontWeight: 800, color: c }}>{v}</p>
        </div>
      ))}
      {/* 7-day rolling weighted-pipeline sparkline — shows heating/cooling */}
      {Array.isArray(m.forecast_spark) && m.forecast_spark.length > 0 && (
        <div data-testid="money-forecast-trend" title={`7-day weighted pipeline trend · ${m.forecast_trend} · Δ$${Math.round(m.forecast_delta || 0).toLocaleString()}`}>
          <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>
            7-day trend <span data-testid="money-forecast-arrow" style={{ color: trendColor, marginLeft: 4 }}>{trendArrow}</span>
          </p>
          <Spark data={m.forecast_spark} color={trendColor} w={72} h={18} />
        </div>
      )}
      <div>
        <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>Unpaid</p>
        <p data-testid="money-unpaid" style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: SUB }}>{m.unpaid_invoices == null ? 'not tracked yet' : ctx.demo ? '$•••' : `$${m.unpaid_invoices}`}</p>
      </div>
      <div>
        <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>Replies · wk {m.replies_week || 0}</p>
        <Spark data={m.reply_spark || [0, 0, 0, 0, 0, 0, 0, 0]} color="#7BB0EC" />
      </div>
    </div>
  );
};

export const ParkingTile = ({ ctx }) => {
  const ideas = ctx.board?.parking || [];
  if (!ideas.length) return <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, margin: 0 }}>Empty — park ideas so they stop renting space in your head.</p>;
  return (
    <div data-testid="desk-parking" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {ideas.map((i) => (
        <span key={i.item_id} data-testid={`parked-${i.item_id}`} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, background: 'rgba(236,124,36,0.08)', border: '1px solid rgba(236,124,36,0.3)', borderRadius: 999, padding: '4px 11px' }}>
          💡 <span onClick={() => !ctx.demo && ctx.setEditItem({ ...i })} style={{ cursor: ctx.demo ? 'default' : 'text' }}>{ctx.dm(i.title)}</span> <DemoBadge seed={i.seed} />
          <button className="no-drag" onClick={() => ctx.setState(i, 'today', `⚡ Promoted "${i.title}" to today`)} title="promote" style={{ background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: 11, padding: 0 }}>↑</button>
          <button className="no-drag" onClick={() => ctx.setState(i, 'archived', `⤓ Archived "${i.title}"`)} title="archive" style={{ background: 'none', border: 'none', color: SUB, cursor: 'pointer', fontSize: 11, padding: 0 }}>⤓</button>
        </span>
      ))}
    </div>
  );
};

export const WinsTile = ({ ctx }) => {
  const wins = ctx.board?.wins || [];
  const open = ctx.board?.open_list || [];
  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {ctx.anim && <span className="desk-accent-sheen" style={{ animationDelay: '3s' }} />}
      <div data-testid="desk-wins" style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        {wins.map((w) => (
          <span key={w.item_id} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 700, color: TXT, border: '1px solid transparent', backgroundImage: `linear-gradient(#17181B, #17181B), ${SPEC}`, backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', borderRadius: 10, padding: '6px 11px' }}>
            <span style={{ color: TEAL }}>✓</span> {ctx.dm(w.title)}{w.value ? <b style={{ color: AMBER }}>· {ctx.demo ? '$•••' : `$${w.value.toLocaleString()}`}</b> : ''} <DemoBadge seed={w.seed} />
          </span>
        ))}
        {!wins.length && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, margin: 0 }}>First win lands here.</p>}
      </div>
      {open.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <p style={{ margin: '0 0 4px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>Open tasks & notes</p>
          {open.map((i) => (
            <div key={i.item_id} style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '3px 0' }}>
              <span style={{ fontSize: 11 }}>{TYPE_META[i.type]?.[0] || '•'}</span>
              <p onClick={() => !ctx.demo && ctx.setEditItem({ ...i })} style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: ctx.demo ? 'default' : 'text' }}>{ctx.dm(i.title)} <DemoBadge seed={i.seed} /></p>
              <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, color: i.heat === 'hot' ? MAGENTA : SUB }}>{Math.round(i.stale_days)}d</span>
              <button data-testid={`open-done-${i.item_id}`} className="no-drag" onClick={(e) => ctx.markDone(i, e)} style={{ background: 'none', border: '1px solid rgba(84,209,151,0.4)', borderRadius: 999, color: TEAL, fontSize: 9.5, padding: '1px 7px', cursor: 'pointer' }}>✓ done</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const NotesTile = ({ ctx }) => {
  const [text, setText] = useState(ctx.notes || '');
  const t = useRef(null);
  useEffect(() => { setText(ctx.notes || ''); }, [ctx.notes]);
  const onChange = (v) => {
    setText(v);
    clearTimeout(t.current);
    t.current = setTimeout(() => ctx.saveNotes(v), 900);
  };
  if (ctx.demo) return <p data-testid="notes-demo-hidden" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, margin: 0 }}>📝 Notes hidden while presenting.</p>;
  return (
    <textarea data-testid="notes-tile-input" className="no-drag studio-scroll" value={text} onChange={(e) => onChange(e.target.value)}
      placeholder="Scratchpad — anything, unstructured, autosaves…"
      style={{ width: '100%', height: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', outline: 'none', resize: 'none', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, lineHeight: 1.6, color: TXT }} />
  );
};

export const CapturePadTile = ({ ctx }) => {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async (type) => {
    if (!val.trim() || busy) return;
    setBusy(true);
    await ctx.capture(val.trim(), type);
    setVal(''); setBusy(false);
  };
  return (
    <div className="no-drag">
      <input data-testid="capture-pad-input" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()}
        placeholder="Quick capture — Enter files it" disabled={busy}
        style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '9px 12px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: TXT, outline: 'none' }} />
      <div style={{ display: 'flex', gap: 5, marginTop: 7, flexWrap: 'wrap' }}>
        {Object.entries(TYPE_META).map(([tp, [ic]]) => (
          <button key={tp} data-testid={`capture-pad-chip-${tp}`} onClick={() => go(tp)} disabled={busy || !val.trim()}
            style={{ ...mini, padding: '2px 9px', fontSize: 10, opacity: !val.trim() ? 0.45 : 1 }}>{ic} +{tp}</button>
        ))}
      </div>
    </div>
  );
};

/** Desk AI — INFORMER, never pusher. Curator lines in-tile, acts on command. */
export const DeskAITile = ({ ctx }) => {
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const isFriday = new Date().getDay() === 5;
  const curator = [];
  const pulse = ctx.board?.pulse || [];
  const reply = pulse.find((p) => p.kind === 'reply');
  const quote = pulse.find((p) => p.kind === 'quote' && p.hot);
  if (reply) curator.push(`↩ ${reply.title} — warm, worth a look.`);
  if (quote) curator.push(`📄 ${quote.title} is heating (${quote.sub}).`);
  if (isFriday) curator.push('🧹 It\'s Friday — sweep the parking lot when you\'re ready.');
  const send = async (text, sweep) => {
    const msg = (text || input).trim(); if (!msg || busy) return;
    setInput(''); setChat((c) => [...c, { role: 'user', content: msg }]); setBusy(true);
    try {
      const d = await ctx.req('POST', '/api/admin/desk/ai', { message: msg, sweep: !!sweep, history: chat.slice(-8) });
      setChat((c) => [...c, { role: 'assistant', content: d.reply, offer: d.offer_compose_lead }]);
      if (d.executed?.length) { ctx.showToast(`✦ Desk AI: ${d.executed.map((e) => e.op).join(', ')}`, async () => { await ctx.req('POST', '/api/admin/desk/ai/undo', { executed: d.executed }); ctx.load(); }); ctx.load(); }
    } catch (e) { setChat((c) => [...c, { role: 'assistant', content: 'Hit a snag: ' + e.message }]); }
    setBusy(false);
  };
  return (
    <div className="no-drag" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexShrink: 0 }}>
        <span data-testid="desk-ai-orb" className={ctx.anim ? 'desk-orb-breathe' : ''} style={{ width: 18, height: 18, borderRadius: '50%', background: SPEC, flexShrink: 0, boxShadow: busy ? `0 0 16px ${VIOLET}` : 'none' }} />
        <p style={{ margin: 0, flex: 1, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB }}>informer, not pusher — reads when you look</p>
        {isFriday && <button data-testid="friday-sweep-btn" onClick={() => send('Run the Friday sweep — walk me through the parked ideas and stale items.', true)} style={{ ...mini, padding: '2px 10px', fontSize: 10, color: AMBER, borderColor: 'rgba(247,150,64,0.45)' }}>🧹 Sweep</button>}
      </div>
      {curator.length > 0 && (
        <div data-testid="desk-ai-curator" style={{ flexShrink: 0, marginBottom: 7, borderLeft: `2px solid ${VIOLET}`, paddingLeft: 9 }}>
          {curator.slice(0, 3).map((l, i) => <p key={i} style={{ margin: '2px 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, opacity: 0.85 }}>{ctx.dm(l)}</p>)}
        </div>
      )}
      <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginBottom: 8 }}>
        {chat.length === 0 && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, margin: 0 }}>{ctx.demo ? 'Chat hidden while presenting — new session.' : 'Dump anything, or ask — "add the sand filter quote to a list", "archive that stale task". Undo on everything.'}</p>}
        {chat.map((c, i) => (
          <div key={i} data-testid={`desk-ai-msg-${i}`} style={{ margin: '6px 0', textAlign: c.role === 'user' ? 'right' : 'left' }}>
            <span style={{ display: 'inline-block', maxWidth: '92%', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, lineHeight: 1.5, color: TXT, background: c.role === 'user' ? 'rgba(236,124,36,0.18)' : 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '7px 11px', textAlign: 'left' }}>{ctx.dm(c.content)}</span>
            {c.offer && <button data-testid={`desk-ai-offer-${i}`} onClick={() => ctx.composeFor(c.offer)} style={{ ...mini, display: 'block', marginTop: 4, color: TEAL, borderColor: 'rgba(84,209,151,0.4)', fontSize: 10.5 }}>✉ Draft the opener →</button>}
          </div>
        ))}
        {busy && <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: VIOLET, margin: 0 }}>thinking…</p>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <input data-testid="desk-ai-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Tell me anything…" disabled={busy}
          style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '8px 11px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: TXT, outline: 'none' }} />
        <button data-testid="desk-ai-send" onClick={() => send()} disabled={busy || !input.trim()} style={{ ...mini, color: TEAL, borderColor: 'rgba(84,209,151,0.4)', opacity: busy || !input.trim() ? 0.5 : 1 }}>Send</button>
      </div>
    </div>
  );
};
